/**
 * Banca sul telefono (7/10/2026): risponde a "cosa è passato sul conto?".
 *
 * 26/10/2026 ("alleggerire", come il computer): in cima quanto si può ancora spendere col fido
 * Qonto; niente più le tre pillole; i bonifici da abbinare sono una riga "Da fare" (quando ci
 * sono) e il commercialista sta nel menu ⋯. Le due liste si aprono col `?vista=` di sempre.
 *
 * Com'era il 7/10:
 *
 * Prima, a 390px: una frase di quattro righe, quattro riquadri di saldi, poi
 * "Bonifici da abbinare", che dal telefono è una riga "si fa dal computer", e
 * per arrivare ai movimenti bisognava cambiare linguetta.
 * Qui la forma è L'ESTRATTO CONTO di un'app della banca:
 *   1. in grande, il saldo del conto principale (si tocca e salgono tutti i
 *      conti: lì si correggono anche i contanti in cassa);
 *   2. le viste: movimenti (la prima, sul telefono), bonifici da abbinare,
 *      da comunicare al commercialista;
 *   3. movimenti: un gruppo per giorno ("Oggi", "Ieri", "Lunedì 5 ottobre"),
 *      una riga per movimento: chi, a cosa è collegato, quanto. Si tocca e
 *      sale il pannello: collega a un documento, scollega;
 *   4. da abbinare: i pagamenti che si sono segnati da soli (si possono
 *      rimettere com'erano); confrontare bonifico e fattura resta al computer,
 *      e lo dice una riga;
 *   5. per il commercialista: incassi e pagamenti da comunicare, con "Copia
 *      l'elenco" in fondo.
 * Sul computer la vista predefinita è "Bonifici da abbinare"; sul telefono è
 * "Movimenti", perché dal telefono si controlla se un bonifico è arrivato. Le
 * pillole scrivono sempre `?vista=` per intero, quindi un link copiato dal
 * telefono apre la stessa vista anche sul computer.
 * Stesse chiamate e stessa cache del computer.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon } from '../../../components/ui/Icon';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../../components/ui/OrdinaFiltra';
import { MCampo, MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, iniziali, leggibile } from '../../../components/mobile/MobileKit';
import { useUrlState } from '../../../hooks/useUrlState';
import { accountsQuery, canWriteAdmin, plurale } from '../adminApi';
import { CollegaPopover } from '../MovimentiTab';
import { copiaElenco } from '../ChiuseQuiTab';
import { AdminHeader, useStorico, useSyncAll } from '../shared/AdminShell';
import { Fatto, Foglio, Gesto, StoricoAcceso, Testo, VisteInVista, aGruppi, euroEsatto, euroRiga, giornoBreve, giornoGruppo, nomeRiga } from '../shared/AdminMobile';
import {
  COLLEGATO_LABEL, COME_LABEL, RIMETTI_LABEL, TIPO_AUTOMATICO_LABEL, automaticiQuery, bancaSintesiQuery, commercialistaQuery,
  fmtSaldo, meseInParole, movimentiQuery, parseEuro, quandoAggiornata, tempoFa, useBancaActions,
  type BancaSintesi, type MovimentoConto, type PagamentoAutomatico, type PagamentoDaComunicare,
} from './bancaApi';

type Vista = 'movimenti' | 'controllare' | 'commercialista';
const PAGINA = 25;

/** Lo stato dei parametri che sopravvive al cambio di vista: lo storico è della pagina. */
const TIENI = ['storico'];

export function BancaMobile() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [raw] = useUrlState<string>('vista', '');
  const [, setParams] = useSearchParams();
  const vista: Vista = raw === 'controllare' || raw === 'commercialista' ? raw : 'movimenti';
  const scegli = (k: Vista) => setParams((prev) => {
    const sp = new URLSearchParams();
    for (const t of TIENI) { const v = prev.get(t); if (v) sp.set(t, v); }
    sp.set('vista', k);
    return sp;
  });
  const storico = useStorico();
  const sync = useSyncAll();
  const { data: s } = useQuery(bancaSintesiQuery);
  const [conti, setConti] = useState(false);

  const daAbbinare = s ? s.bonifici.sicuri + s.bonifici.incerti : undefined;
  // 26/10/2026, "alleggerire": niente più le tre pillole. La pagina sono i movimenti; i bonifici
  // da abbinare sono una riga "da fare" in cima (quando ci sono), il commercialista sta nel menu ⋯.
  // Le due liste si aprono con lo stesso `?vista=` di prima e si torna indietro con un tocco.
  const indietro = (
    <button type="button" className="amk-indietro" onClick={() => scegli('movimenti')}>
      <Icon name="chevronL" size={14} /> Movimenti del conto
    </button>
  );
  const commercialista = {
    key: 'commercialista', label: `Pagamenti da comunicare al commercialista${s?.commercialista ? ` (${s.commercialista})` : ''}`,
    icon: 'file' as const, onClick: () => scegli('commercialista'), hidden: vista === 'commercialista',
  };

  return (
    <div className="page adm-page">
      <AdminHeader title="Banca" menu={[...(vista === 'movimenti' ? [storico.menuItem] : []), commercialista, sync.menuItem]} />
      <MPage>
        {s && vista === 'movimenti' && <Risposta s={s} onConti={() => setConti(true)} />}
        {vista === 'movimenti' && storico.on && <StoricoAcceso year={storico.year} onOff={() => storico.set(false)} />}
        {vista === 'movimenti' && (daAbbinare ?? 0) > 0 && (
          <MGroup title="Da fare">
            <MRow icon="link" tone="alert" title={daAbbinare === 1 ? 'Un bonifico da abbinare' : `${daAbbinare} bonifici da abbinare`}
              sub="a una fattura: si abbinano dal computer" onClick={() => scegli('controllare')} />
          </MGroup>
        )}
        {vista === 'movimenti' && <Movimenti viste={null} canWrite={canWrite} storico={storico.on} />}
        {vista === 'controllare' && <DaAbbinare viste={indietro} s={s} canWrite={canWrite} />}
        {vista === 'commercialista' && <Commercialista viste={indietro} />}
      </MPage>
      {conti && s && <FoglioConti s={s} canWrite={canWrite} onClose={() => setConti(false)} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La risposta: il saldo
// ════════════════════════════════════════════════════════════════════

function Risposta({ s, onConti }: { s: BancaSintesi; onConti: () => void }) {
  const navigate = useNavigate();
  if (!s.qontoCollegato) {
    return <MHero label="Banca" value="Non collegata" sub="Qonto si collega dalle Impostazioni: senza, non arrivano né i saldi né i movimenti." onClick={() => navigate('/settings')} />;
  }
  const altri = (s.iva?.saldo ?? 0) + (s.contanti?.saldo ?? 0) + s.altri.saldo;
  // 26/10/2026: la risposta è quanto si può ancora spendere col fido Qonto (come sul computer);
  // il saldo del conto principale sta nella riga sotto. Si tocca e salgono tutti i conti.
  if (s.principale) {
    const spendibile = s.principale.saldo + (s.fido ?? 5000);
    return (
      <MHero
        label="Col fido possiamo ancora spendere"
        unit="€"
        value={fmtSaldo(spendibile)}
        alert={spendibile <= 0}
        sub={<>sul conto principale € {fmtSaldo(s.principale.saldo)}{s.aggiornataAlle ? `, aggiornato ${quandoAggiornata(s.aggiornataAlle)}` : ''}</>}
        onClick={onConti}
      />
    );
  }
  return (
    <MHero
      label="Sui conti"
      unit="€"
      value={fmtSaldo(altri)}
      sub={s.aggiornataAlle ? `aggiornato ${quandoAggiornata(s.aggiornataAlle)}` : 'saldo non ancora aggiornato'}
      onClick={onConti}
    />
  );
}

/** Tutti i conti, uno per riga; i contanti si contano a mano e si correggono da qui. */
function FoglioConti({ s, canWrite, onClose }: { s: BancaSintesi; canWrite: boolean; onClose: () => void }) {
  const { correggiContanti } = useBancaActions();
  const [cassa, setCassa] = useState<string | null>(null);
  const salva = () => {
    const n = parseEuro(cassa ?? '');
    if (!Number.isFinite(n)) return;
    setCassa(null);
    if (s.contanti && Math.round(n * 100) !== Math.round(s.contanti.saldo * 100)) correggiContanti.mutate(n);
  };
  return (
    <>
      <Foglio open={cassa === null} onClose={onClose} title="I conti">
        <div className="mk-list">
          {s.principale && <MRow icon="landmark" title="Conto principale" sub={s.principale.ibanFinale ? `Qonto, l'IBAN finisce con ${s.principale.ibanFinale}` : 'Qonto'} trail={`€ ${fmtSaldo(s.principale.saldo)}`} />}
          {s.iva && <MRow icon="percent" title={`Conto ${s.iva.nome}`} sub="i soldi messi da parte per l'IVA" trail={`€ ${fmtSaldo(s.iva.saldo)}`} />}
          {s.contanti && (
            <MRow icon="banknote" title="Contanti in cassa" sub="si contano a mano"
              trail={`€ ${fmtSaldo(s.contanti.saldo)}`}
              onClick={canWrite ? () => setCassa(String(s.contanti!.saldo).replace('.', ',')) : undefined} />
          )}
          {s.altri.nomi.length > 0 && <MRow icon="wallet" title="Altri conti Qonto" sub={s.altri.nomi.join(', ')} trail={`€ ${fmtSaldo(s.altri.saldo)}`} />}
        </div>
        <div className="mk-group-note">Quello che entra ed esce nei prossimi mesi, giorno per giorno, sta nel Calendario dei pagamenti.</div>
      </Foglio>
      <MCampo
        open={cassa !== null}
        onClose={() => setCassa(null)}
        titolo="Contanti in cassa oggi"
        nota="Quanti ce ne sono adesso, contati a mano. Il saldo di partenza si sposta di quanto serve."
        campo={<input className="inp" inputMode="decimal" autoFocus aria-label="Contanti in cassa oggi, in euro" value={cassa ?? ''} onChange={(e) => setCassa(e.target.value)} />}
        onSalva={salva}
      />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Movimenti
// ════════════════════════════════════════════════════════════════════

/** Gli stessi ordini del computer: li fa l'API (`sort`). */
const ORDINI: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Movimenti più recenti prima' },
  { key: 'vecchi', label: 'Movimenti più vecchi prima' },
  { key: 'alti', label: 'Prima gli importi più alti' },
  { key: 'bassi', label: 'Prima gli importi più bassi' },
];

/** Come si è mosso il denaro, con parole intere (il tipo di operazione di Qonto). */
const COME_OPERAZIONE: Record<string, string> = {
  card: 'Carta',
  direct_debit: 'Addebito automatico',
  transfer: 'Bonifico',
  income: 'Bonifico in entrata',
  pay_later: 'Pagamento a rate di Qonto',
  qonto_fee: 'Commissione di Qonto',
};

/** Cosa c'è dietro un movimento, in una riga: il documento collegato o lo stipendio. */
function collegatoA(t: MovimentoConto): string | null {
  if (t.collegato) return t.collegato.cosa ? `${COLLEGATO_LABEL[t.collegato.tipo]}: ${t.collegato.cosa}` : COLLEGATO_LABEL[t.collegato.tipo];
  // Chi è lo dice già il titolo della riga: qui "Stipendio di settembre".
  if (t.stipendio) return t.stipendio.mese ? `${t.stipendio.etichetta} di ${meseInParole(t.stipendio.mese).split(' ')[0]}` : t.stipendio.etichetta;
  return null;
}

const importoConSegno = (t: MovimentoConto) => (t.amount == null ? 'riservato' : `${t.side === 'CREDIT' ? '+' : '−'}${euroRiga(t.amount).replace('−', '')}`);

function Movimenti({ viste, canWrite, storico }: { viste: ReactNode; canWrite: boolean; storico: boolean }) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [accountId] = useUrlState<string>('acc', '');
  const [from, setFrom] = useUrlState<string>('from', '');
  const [to, setTo] = useUrlState<string>('to', '');
  const { data: accounts } = useQuery(accountsQuery);
  const ordina = useOrdina(ORDINI);
  // Le stesse chiavi del computer (side, unmatched, acc): i link di Spese e Fatture ("Scegli il bonifico") funzionano qui.
  const defs = useMemo<FiltroDef[]>(() => {
    const qonto = (accounts ?? []).filter((a) => a.provider === 'QONTO');
    return [
      { param: 'side', label: 'Entrate o uscite', opzioni: [{ value: 'CREDIT', label: 'Solo le entrate' }, { value: 'DEBIT', label: 'Solo le uscite' }] },
      { param: 'unmatched', label: 'Documento collegato', opzioni: [{ value: '1', label: 'Solo i movimenti senza documento' }] },
      ...(qonto.length > 1 ? [{ param: 'acc', label: 'Conto', opzioni: qonto.map((a) => ({ value: a.id, label: a.name })) }] : []),
    ];
  }, [accounts]);
  const filtri = useFiltri(defs);
  const side = filtri.valore('side');
  const [quante, setQuante] = useState(PAGINA);
  const [sel, setSel] = useState<MovimentoConto | null>(null);
  const [collega, setCollega] = useState<MovimentoConto | null>(null);
  const { scollegaMovimento } = useBancaActions();

  const q = useQuery({
    ...movimentiQuery({
      accountId: accountId || undefined, from: from || undefined, to: to || undefined,
      side: side === 'DEBIT' || side === 'CREDIT' ? side : '', senzaDocumento: filtri.valore('unmatched') === '1',
      search: search.trim() || undefined, sort: ordina.key, includeHistory: storico, page: 1, pageSize: quante,
    }),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const perGiorno = ordina.key === 'recenti' || ordina.key === 'vecchi';
  const gruppi = perGiorno ? aGruppi(items, (t) => t.settledOn ?? '') : [{ chiave: 'tutti', items }];

  const riga = (t: MovimentoConto) => {
    const nome = t.counterparty || t.label;
    const coll = collegatoA(t);
    return (
      <MRow
        key={t.id}
        text={iniziali(nome)}
        round
        title={nomeRiga(nome)}
        sub={coll ?? <span className="amk-manca">nessun documento</span>}
        trail={importoConSegno(t)}
        trailTone={t.amount == null ? 'mute' : undefined}
        onClick={() => setSel(t)}
      />
    );
  };

  return (
    <>
      <div className="amk-viste">
        {viste}
        <div className="amk-tools">
          <MSearch value={search} onChange={setSearch} placeholder="Nome o descrizione" />
          <OrdinaFiltra ordina={ordina} filtri={filtri} label="Filtra" />
        </div>
      </div>

      {(from || to) && (
        <div className="mk-list">
          <MRow icon="calendar" title={from && to ? `Dal ${giornoBreve(from)} al ${giornoBreve(to)}` : from ? `Dal ${giornoBreve(from)} in poi` : `Fino al ${giornoBreve(to)}`}
            sub="Tocca per vedere tutti i movimenti" onClick={() => { setFrom(''); setTo(''); }} chevron={false} />
        </div>
      )}

      {q.isLoading && !q.data ? (
        <MGroup title="Movimenti"><MEmpty>Carico i movimenti…</MEmpty></MGroup>
      ) : items.length === 0 ? (
        <MGroup title="Movimenti">
          <MEmpty>{search.trim() || filtri.attivi || from || to ? 'Nessun movimento con questa ricerca o questi filtri.' : 'Nessun movimento: arrivano da Qonto con "Aggiorna banca e fatture" (menu ⋯).'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g, i) => (
        <MGroup key={g.chiave} title={perGiorno ? giornoGruppo(g.chiave) : ordina.attiva.label} count={g.items.length}>
          {g.items.map(riga)}
          {i === gruppi.length - 1 && total > items.length && (
            <MMore label={`Vedi altri ${Math.min(PAGINA, total - items.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />
          )}
        </MGroup>
      ))}

      {sel && (
        <FoglioMovimento t={sel} canWrite={canWrite} onClose={() => setSel(null)}
          onCollega={() => { const t = sel; setSel(null); setCollega(t); }}
          onScollega={() => {
            const t = sel;
            setSel(null);
            scollegaMovimento.mutate({ transactionId: t.id, label: `${giornoBreve(t.settledOn)} · ${t.counterparty || t.label}` });
          }} />
      )}
      {collega && <CollegaPopover t={collega} onClose={() => setCollega(null)} />}
    </>
  );
}

function FoglioMovimento({ t, canWrite, onClose, onCollega, onScollega }: {
  t: MovimentoConto; canWrite: boolean; onClose: () => void; onCollega: () => void; onScollega: () => void;
}) {
  const entrata = t.side === 'CREDIT';
  const coll = collegatoA(t);
  const puoCollegare = canWrite && !t.riservato && !t.match && !t.stipendio;
  return (
    <Foglio open onClose={onClose} title={leggibile(t.counterparty || t.label)}
      primary={puoCollegare ? <button className="btn btn-primary m-block" onClick={onCollega}><Icon name="link" size={12} /> Collega a un documento</button> : undefined}>
      <div className="mk-list">
        <Fatto label={entrata ? 'Entrati' : 'Usciti'} value={t.amount == null ? 'riservato' : euroEsatto(t.amount)} />
        <Fatto label="Quando" value={<>{giornoBreve(t.settledOn) || '—'}<span className="s">{tempoFa(t.settledOn)}</span></>} />
        <Fatto label="Come" value={COME_OPERAZIONE[t.operationType ?? ''] ?? 'Movimento in banca'} />
        {t.accountName && <Fatto label="Conto" value={t.accountName} />}
        <Fatto label="Collegato a" value={coll ?? 'nessun documento'} />
      </div>
      {t.counterparty && t.label && t.label.trim() !== t.counterparty.trim() && (
        <div className="mk-list"><Testo>{t.label}</Testo></div>
      )}
      {canWrite && !t.riservato && t.match && (
        <div className="mk-list">
          <Gesto icon="unlink" label="Scollega il documento" sub="il movimento torna senza documento; la fattura o la voce restano come sono" onClick={onScollega} danger />
        </div>
      )}
    </Foglio>
  );
}

// ════════════════════════════════════════════════════════════════════
// Da abbinare
// ════════════════════════════════════════════════════════════════════

function DaAbbinare({ viste, s, canWrite }: { viste: ReactNode; s: BancaSintesi | undefined; canWrite: boolean }) {
  const q = useQuery(automaticiQuery);
  const items = q.data?.items ?? [];
  const [sel, setSel] = useState<PagamentoAutomatico | null>(null);
  const { annullaAutomatico } = useBancaActions();
  const n = s ? s.bonifici.sicuri + s.bonifici.incerti : 0;
  return (
    <>
      {viste}
      <MGroup title="Bonifici da abbinare" count={n || undefined}>
        {n > 0 ? (
          <MRow icon="swap" title={plurale(n, 'bonifico somiglia a una fattura', 'bonifici somigliano a una fattura')}
            sub="Si abbinano dal computer: bonifico e fattura vanno confrontati riga per riga." subWrap />
        ) : (
          <MEmpty>Nessun bonifico da abbinare: ogni pagamento che somiglia a una fattura è già collegato.</MEmpty>
        )}
      </MGroup>

      <MGroup title="Si sono segnati da soli" count={items.length || undefined}
        note={items.length ? `Negli ultimi ${q.data?.giorni ?? 30} giorni, solo qui dentro e non su FattureInCloud. Se uno è sbagliato, toccalo e rimettilo com'era.` : undefined}>
        {q.isLoading && !q.data ? <MEmpty>Carico…</MEmpty>
          : items.length === 0 ? <MEmpty>Negli ultimi 30 giorni nessun pagamento si è segnato da solo.</MEmpty>
          : items.map((v) => (
            <MRow
              key={`${v.runKey}-${v.key}`}
              text={iniziali(v.nome)}
              round
              title={nomeRiga(v.nome)}
              sub={`${tempoFa(v.bonifico.data)} · ${v.tipo === 'SPESA_FISSA' ? 'spesa fissa' : v.cosa}`}
              trail={euroRiga(v.importo)}
              onClick={() => setSel(v)}
            />
          ))}
      </MGroup>

      {sel && (
        <Foglio open onClose={() => setSel(null)} title={leggibile(sel.nome)}
          primary={canWrite ? (
            <button className="btn m-block" disabled={annullaAutomatico.isPending} onClick={() => { const v = sel; setSel(null); annullaAutomatico.mutate(v); }}>
              <Icon name="undo" size={12} /> {RIMETTI_LABEL[sel.tipo]}
            </button>
          ) : undefined}>
          <div className="mk-list">
            <Fatto label="Cos'è" value={<>{TIPO_AUTOMATICO_LABEL[sel.tipo]}<span className="s">{sel.cosa}</span></>} />
            <Fatto label="Importo" value={<>{euroEsatto(sel.importo)}{sel.previsto != null && Math.round(sel.previsto * 100) !== Math.round(sel.importo * 100) && <span className="s">previsti {euroEsatto(sel.previsto)}</span>}</>} />
            <Fatto label="Il movimento" value={<>{leggibile(sel.bonifico.nome)}<span className="s">{giornoBreve(sel.bonifico.data)}{sel.bonifico.conto ? `, ${sel.bonifico.conto}` : ''}</span></>} />
            <Fatto label="Segnata" value={tempoFa(sel.segnataIl.slice(0, 10))} />
          </div>
          <div className="mk-group-note">Rimettendola com'era il movimento si stacca e non si abbina più da solo.</div>
        </Foglio>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Da comunicare al commercialista
// ════════════════════════════════════════════════════════════════════

function Commercialista({ viste }: { viste: ReactNode }) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const q = useQuery(commercialistaQuery(search));
  const items = q.data?.items ?? [];
  const [sel, setSel] = useState<PagamentoDaComunicare | null>(null);
  const incassi = items.filter((r) => r.lato === 'INCASSO');
  const uscite = items.filter((r) => r.lato === 'USCITA');
  const rata = (r: PagamentoDaComunicare) => (r.rateTotali > 1 ? `, rata ${r.rata} di ${r.rateTotali}` : '');
  const riga = (r: PagamentoDaComunicare) => (
    <MRow
      key={`${r.lato}-${r.id}`}
      text={iniziali(r.nome)}
      round
      title={nomeRiga(r.nome)}
      sub={`${giornoBreve(r.pagataIl)} · ${r.numero ?? 'senza numero'}${rata(r)}`}
      trail={euroRiga(r.importo)}
      onClick={() => setSel(r)}
    />
  );
  return (
    <>
      <div className="amk-viste">
        {viste}
        {(items.length > 6 || search.trim()) && <MSearch value={search} onChange={setSearch} placeholder="Cliente, fornitore o numero" />}
      </div>

      {q.isLoading && !q.data ? <MGroup title="Da comunicare"><MEmpty>Carico…</MEmpty></MGroup>
        : items.length === 0 ? (
          <MGroup title="Da comunicare">
            <MEmpty>{search.trim() ? 'Nessun pagamento con questa ricerca.' : 'Niente da comunicare: ogni pagamento segnato qui risulta anche su FattureInCloud.'}</MEmpty>
          </MGroup>
        ) : (
          <>
            {incassi.length > 0 && (
              <MGroup title="Incassi dai clienti" count={incassi.length}
                note={uscite.length ? undefined : `In tutto ${euroEsatto(q.data?.totali.incassi ?? 0)}. Su FattureInCloud risultano ancora da pagare: quando lo studio le registra, spariscono da qui.`}>
                {incassi.map(riga)}
              </MGroup>
            )}
            {uscite.length > 0 && (
              <MGroup title="Pagamenti ai fornitori" count={uscite.length}
                note="Su FattureInCloud risultano ancora da pagare: quando lo studio le registra, spariscono da qui.">
                {uscite.map(riga)}
              </MGroup>
            )}
          </>
        )}

      {/* L'azione della vista in basso, dove arriva il pollice (mobile.md §4). */}
      {items.length > 0 && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => { void copiaElenco(items, !!search.trim()); }}>
            <Icon name="copy" size={12} /> Copia l'elenco per il commercialista
          </button>
        </div>
      )}

      {sel && (
        <Foglio open onClose={() => setSel(null)} title={leggibile(sel.ragioneSociale)}>
          <div className="mk-list">
            <Fatto label="Fattura" value={`${sel.numero ?? 'senza numero'}${rata(sel)}`} />
            <Fatto label={sel.lato === 'INCASSO' ? 'Incassata il' : 'Pagata il'} value={giornoBreve(sel.pagataIl) || '—'} />
            <Fatto label="Importo" value={euroEsatto(sel.importo)} />
            <Fatto label="Come l'abbiamo segnata" value={COME_LABEL[sel.come]} />
            <Fatto label="Bonifico" value={sel.bonifico ? <>{leggibile(sel.bonifico.nome)}{sel.bonifico.conto && <span className="s">{sel.bonifico.conto}</span>}</> : 'nessuno collegato'} />
          </div>
          {sel.perche && <div className="mk-list"><Testo>{sel.perche}</Testo></div>}
        </Foglio>
      )}
    </>
  );
}
